'use client';

import { formSelectField } from '@lib/form/helper';
import * as RadixForm from '@radix-ui/react-form';
import {
  InputFieldError,
  InputFieldLabel,
  InputFieldNote,
} from '@tuic/web-compositions/shared/InputField';
import { Select } from '@tuic/web-compositions/shared/Select';

import type { AnyFieldApi } from '@tanstack/react-form';
import type { FC, OptionHTMLAttributes, ReactNode } from 'react';

type SelectOption = OptionHTMLAttributes<HTMLOptionElement>;

export type ManifestSelectFieldProps = {
  field: AnyFieldApi;
  label?: ReactNode;
  note?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  options?: SelectOption[];
  placeholder?: string;
  clearable?: boolean;
  clearLabel?: string;
};

export const ManifestSelectField: FC<ManifestSelectFieldProps> = ({
  field,
  label,
  note,
  required,
  disabled,
  className,
  options,
  placeholder,
  clearable,
  clearLabel,
}) => {
  const { onChange, value, name, error } = formSelectField(field);

  const finalOptions = clearable
    ? [
        { value: '', children: clearLabel ?? placeholder ?? '—' },
        ...(options ?? []),
      ]
    : options;

  return (
    <RadixForm.Field name={field.name} className={className} asChild>
      <div>
        <label className="block space-y-1">
          {label && (
            <RadixForm.Label asChild>
              <InputFieldLabel asChild>
                <div>
                  {label}
                  {required ? '*' : ''}
                </div>
              </InputFieldLabel>
            </RadixForm.Label>
          )}
          <Select
            name={name}
            options={finalOptions}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onChange={onChange}
          />
          {error ? (
            <InputFieldError>{error}</InputFieldError>
          ) : (
            note && <InputFieldNote>{note}</InputFieldNote>
          )}
        </label>
      </div>
    </RadixForm.Field>
  );
};
